
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FileText, ArrowRightLeft, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface QuickbooksIntegrationSettingsProps {
  initialConnected?: boolean;
  onSaved: () => Promise<void>;
}

const QuickbooksIntegrationSettings: React.FC<QuickbooksIntegrationSettingsProps> = ({ 
  initialConnected = false,
  onSaved 
}) => {
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(initialConnected);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [realmId, setRealmId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | null>(null);

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      // Implement QuickBooks connection logic
      // This would typically be an API call to your backend
      
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      await onSaved();
      setIsConnected(true);
      setLastSynced(new Date().toLocaleString());
      
      toast({
        title: "Connection successful",
        description: "Your QuickBooks account has been connected.",
      });
    } catch (error) {
      toast({
        title: "Connection failed",
        description: "Failed to connect to QuickBooks. Please check your credentials.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsLoading(true);
      // Implement QuickBooks sync logic
      
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      setLastSynced(new Date().toLocaleString());
      
      toast({
        title: "Sync completed",
        description: "Your QuickBooks data has been synchronized.",
      });
    } catch (error) {
      toast({
        title: "Sync failed",
        description: "Failed to sync with QuickBooks.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileText className="mr-2 h-6 w-6" />
          QuickBooks Integration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>QuickBooks Connection</Label>
            <Switch 
              checked={isConnected}
              onCheckedChange={setIsConnected}
            />
          </div>

          {isConnected ? (
            <div className="space-y-4">
              <div>
                <Label>Client ID</Label>
                <Input 
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Enter QuickBooks Client ID"
                />
              </div>
              <div>
                <Label>Client Secret</Label>
                <Input 
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="Enter QuickBooks Client Secret"
                />
              </div>
              <div>
                <Label>Realm ID (Company ID)</Label>
                <Input 
                  value={realmId}
                  onChange={(e) => setRealmId(e.target.value)}
                  placeholder="Enter QuickBooks Realm ID"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  You can find your Realm ID in the QuickBooks Online Account
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button 
                  onClick={handleConnect}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? "Connecting..." : "Update Connection"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleSync}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Now
                </Button>
              </div>
              
              <div className="bg-muted p-3 rounded-md">
                <h4 className="text-sm font-medium mb-2">Sync Data</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Customers</span>
                    <span className="text-xs text-muted-foreground">10 synced</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Invoices</span>
                    <span className="text-xs text-muted-foreground">34 synced</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Payments</span>
                    <span className="text-xs text-muted-foreground">20 synced</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {lastSynced ? `Last synced: ${lastSynced}` : 'Not synced yet'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Connect your QuickBooks account to import customer data and synchronize your accounting.
              </p>
              <div className="bg-muted p-3 rounded-md">
                <h4 className="text-sm font-medium mb-2">Integration Features</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center">
                    <ArrowRightLeft className="h-4 w-4 mr-2 text-primary" />
                    <span>Sync customer data between systems</span>
                  </li>
                  <li className="flex items-center">
                    <ArrowRightLeft className="h-4 w-4 mr-2 text-primary" />
                    <span>Import invoices and payment records</span>
                  </li>
                  <li className="flex items-center">
                    <ArrowRightLeft className="h-4 w-4 mr-2 text-primary" />
                    <span>Automatically update referral payments</span>
                  </li>
                </ul>
              </div>
              <Button onClick={handleConnect} className="w-full">
                Connect to QuickBooks
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default QuickbooksIntegrationSettings;
